import React, { Fragment } from 'react';
import styled, { css } from 'styled-components';
import { remCalc } from '@lux/helpers';

import Anchor from '../Anchor';
import Grid from '../Grid';
import Column from '../Column';
import Row from '../Row';
import Theme from '../../theme';

import Search from './Search';
import Logo from './Logo';

import IconClose from '../../assets/svgs/close.svg';
import IconChevronUp from '../../assets/svgs/chevron-up.svg';
import IconChevronDown from '../../assets/svgs/chevron-down.svg';
import IconMenuBack from '../../assets/svgs/chevron-left.svg';

export const SCHamburgerMenu = styled.div`
  height: 100vh;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  background-color: ${Theme.colours.white};
  border-top: 4px solid ${Theme.colours.singtelred};
  opacity: 0;
  overflow-y: auto;
  visibility: hidden;
  z-index: 99999;

  transition: all 400ms ease-out;
  transform: translate(-200px, 0);

  ${props =>
    props.isHamburgerMenuOpen &&
    css`
      transform: translate(0, 0);
      opacity: 1;
      visibility: visible;
    `};

  ${Theme.media.lg`
  transform: translate(-100vw, 0);
  `};
`;

const SCHamburgerHeader = styled(Row)`
  height: 64px;
`;

const SCMenuCloseIcon = styled(IconClose)`
  cursor: pointer;
  float: right;
`;

const StyledSearchContainer = styled.div`
  margin-bottom: ${remCalc(16)};
`;

const SCColumnPrimaryMenuItem = styled(Column)`
  padding: 0 ${remCalc(8)};
`;

const SCColumnSubMenuItem = styled(SCColumnPrimaryMenuItem)`
  padding-left: ${remCalc(8)};
`;

const SCAnchorBlack = styled(Anchor)`
  color: ${Theme.colours.black};
  font-size: ${Theme.fonts.sizes.small};
  display: block;

  &:not([disabled]) {
    &:hover {
      color: ${Theme.colours.black};
    }
  }

  ${props =>
    props.active &&
    css`
      color: ${Theme.colours.singtelred};

      &:not([disabled]) {
        &:hover {
          color: ${Theme.colours.singtelred};
        }
      }
    `};

  & svg {
    float: right;
  }
`;

const SCColumnSubSubMenuItem = styled(Column)`
  margin-top: ${remCalc(8)};
  padding-left: ${remCalc(32)};
`;

const Divider = styled.div`
  margin: ${remCalc(16)} ${remCalc(-16)};
  border-bottom: 1px solid ${Theme.colours.grey_e2};
`;

const StyledFourthLevelMenuColumn = styled(Column)`
  padding-left: ${remCalc(32)};
`;

const StyledIconColumn = styled(Column)`
  height: ${remCalc(16)};
`;

const StyledIconMenuBack = styled(IconMenuBack)`
  vertical-align: middle;
`;

class Menu extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      thirdLevelMenu: {
        subMenu: []
      }
    };
  }

  onThirdLevelMenuClick = menu => {
    this.setState({
      thirdLevelMenu: menu
    });

    this.props.onThirdLevelMenuClick();
  };

  render() {
    const {
      baseUrl,
      menuItems,
      activeSubMenuItem,
      activeMenuItem,
      onMenuItemClick,
      firstLevelLinks,
      isFourthLevelMenuOpen
    } = this.props;

    const { thirdLevelMenu } = this.state;

    return isFourthLevelMenuOpen ? (
      <Fragment>
        <SCAnchorBlack href={`${baseUrl}${thirdLevelMenu.item.href}`}>
          {thirdLevelMenu.item.title}
        </SCAnchorBlack>
        <Divider />
        {(thirdLevelMenu.subMenu || []).map((menuItem, menuKey) => (
          <Fragment key={menuKey}>
            <Row key={menuKey} middle="sm">
              <StyledFourthLevelMenuColumn sm={12}>
                <SCAnchorBlack
                  secondary
                  href={`${baseUrl}${menuItem.item.href}`}
                >
                  {menuItem.item.title}
                </SCAnchorBlack>
              </StyledFourthLevelMenuColumn>
            </Row>
            <Divider />
          </Fragment>
        ))}
      </Fragment>
    ) : (
      (firstLevelLinks || []).map((menuItem, menuKey) => {
        return (
          <Row key={menuKey} middle="sm">
            {/* First level menu items */}
            <SCColumnPrimaryMenuItem sm={12}>
              <SCAnchorBlack href={`${baseUrl}${menuItem.href}`}>
                {menuItem.title}
              </SCAnchorBlack>
              <Divider />
            </SCColumnPrimaryMenuItem>
            {/* Second level menu items */}
            {menuItem.active &&
              (menuItems || []).map((subMenuItem, subKey) => {
                return (
                  <Column key={subKey} sm={12}>
                    <SCColumnSubMenuItem sm={12}>
                      {/* We display accordion style if submenus exists */}
                      {(subMenuItem.subMenu || []).length > 0 && (
                        <SCAnchorBlack
                          secondary
                          active={subKey === activeSubMenuItem}
                          onClick={() => onMenuItemClick(menuKey, subKey)}
                        >
                          {subMenuItem.item.title}
                          {subKey === activeSubMenuItem && (
                            <IconChevronUp sm={24} />
                          )}
                          {subKey !== activeSubMenuItem && (
                            <IconChevronDown sm={24} />
                          )}
                        </SCAnchorBlack>
                      )}
                      {/* If no submenu is found, its a html href link */}
                      {(subMenuItem.subMenu || []).length === 0 && (
                        <SCAnchorBlack
                          secondary
                          href={`${baseUrl}${subMenuItem.item.href}`}
                        >
                          {subMenuItem.item.title}
                        </SCAnchorBlack>
                      )}
                    </SCColumnSubMenuItem>
                    {/* Third level menu items */}
                    {menuKey === activeMenuItem &&
                      subKey === activeSubMenuItem &&
                      subMenuItem.subMenu
                        .filter(({ subMenu }) => subMenu) // Filtering away empty subMenu
                        .map((subSubMenuItem, subSubKey) => {
                          return (
                            <Fragment key={subSubKey}>
                              {/* Display the fourth level menu if subMenu exists */}
                              {subSubMenuItem.subMenu.length > 0 && (
                                <SCColumnSubSubMenuItem sm={12}>
                                  <SCAnchorBlack
                                    secondary
                                    onClick={() =>
                                      this.onThirdLevelMenuClick(subSubMenuItem)
                                    }
                                  >
                                    {subSubMenuItem.item.title}
                                  </SCAnchorBlack>
                                </SCColumnSubSubMenuItem>
                              )}
                              {/* If no third level menu is found, it's a html href link */}
                              {(subSubMenuItem.subMenu || []).length === 0 && (
                                <SCColumnSubSubMenuItem sm={12}>
                                  <SCAnchorBlack
                                    secondary
                                    href={`${baseUrl}${
                                      subSubMenuItem.item.href
                                    }`}
                                  >
                                    {subSubMenuItem.item.title}
                                  </SCAnchorBlack>
                                </SCColumnSubSubMenuItem>
                              )}
                            </Fragment>
                          );
                        })}
                    <Divider />
                  </Column>
                );
              })}
          </Row>
        );
      })
    );
  }
}

class MobileMenu extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      isFourthLevelMenuOpen: false
    };
  }

  closeFourthLevelMenu = () => {
    this.setState({
      isFourthLevelMenuOpen: false
    });
  };

  onClose = () => {
    this.closeFourthLevelMenu();
    this.props.onClose();
  };

  onThirdLevelMenuClick = () => {
    this.setState({
      isFourthLevelMenuOpen: true
    });
  };

  render() {
    const {
      baseUrl,
      menuItems,
      isHamburgerMenuOpen,
      logoLink,
      logoAlt,
      activeSubMenuItem,
      activeMenuItem,
      onMenuItemClick,
      firstLevelLinks
    } = this.props;

    const { isFourthLevelMenuOpen } = this.state;

    // Disable body scroll when mobile menu is open
    if (typeof document !== 'undefined' && isHamburgerMenuOpen) {
      document.body.style.overflow = 'hidden';
    }

    if (typeof document !== 'undefined' && !isHamburgerMenuOpen) {
      document.body.removeAttribute('style');
    }

    return (
      <SCHamburgerMenu isHamburgerMenuOpen={isHamburgerMenuOpen}>
        <Grid>
          {/* The hamburger menu header */}
          <SCHamburgerHeader middle="sm">
            <StyledIconColumn>
              {isFourthLevelMenuOpen && (
                <StyledIconMenuBack onClick={this.closeFourthLevelMenu} />
              )}
            </StyledIconColumn>
            {/* Logo in the middle */}
            <Column>
              <Logo
                baseUrl={baseUrl}
                link={logoLink}
                alt={logoAlt}
                padded={true}
              />
            </Column>
            {/* The hamburger close icon */}
            <StyledIconColumn>
              <SCMenuCloseIcon onClick={this.onClose} />
            </StyledIconColumn>
          </SCHamburgerHeader>
          <Row>
            <Column sm={12}>
              <StyledSearchContainer>
                <Search isActive={true} />
              </StyledSearchContainer>
            </Column>
          </Row>
          <Row>
            <Column sm={12}>
              {/* Loop through all menu items */}
              <Menu
                baseUrl={baseUrl}
                firstLevelLinks={firstLevelLinks}
                menuItems={menuItems}
                activeSubMenuItem={activeSubMenuItem}
                activeMenuItem={activeMenuItem}
                isFourthLevelMenuOpen={isFourthLevelMenuOpen}
                onMenuItemClick={onMenuItemClick}
                onThirdLevelMenuClick={this.onThirdLevelMenuClick}
              />
            </Column>
          </Row>
        </Grid>
      </SCHamburgerMenu>
    );
  }
}

export default MobileMenu;
